import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { TruckIcon, StoreIcon, WrenchIcon, ArrowRightIcon, DownloadIcon, CheckCircleIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type PartnerCTAProps = {
  language: Language;
};
const partners = {
  ua: [{
    icon: TruckIcon,
    title: "Дистриб'ютори / локальні оптовики",
    description: "Станьте офіційним дистриб'ютором CHAMALEON у вашому регіоні та отримайте ексклюзивні умови співпраці"
  }, {
    icon: StoreIcon,
    title: 'Онлайн / офлайн ритейлери',
    description: 'Розширте асортимент вашого магазину преміальною продукцією з підтримкою маркетингу'
  }, {
    icon: WrenchIcon,
    title: 'Кузовні сервіси (Body Shops)',
    description: 'Професійні рішення для автосервісів з технічною підтримкою та навчанням персоналу'
  }],
  en: [{
    icon: TruckIcon,
    title: 'Distributors / Local Wholesalers',
    description: 'Become an official CHAMALEON distributor in your region and get exclusive partnership terms'
  }, {
    icon: StoreIcon,
    title: 'Online / Offline Retailers',
    description: "Expand your store's range with premium products and marketing support"
  }, {
    icon: WrenchIcon,
    title: 'Body Shops',
    description: 'Professional solutions for auto services with technical support and staff training'
  }]
};
const benefits = {
  ua: ['Ексклюзивні умови', 'Маркетингова підтримка', 'Технічне навчання', 'Швидка логістика'],
  en: ['Exclusive terms', 'Marketing support', 'Technical training', 'Fast logistics']
};
const sectionText = {
  ua: {
    title: 'Станьте партнером CHAMALEON',
    subtitle: 'Приєднуйтесь до мережі успішних партнерів у 80 країнах світу',
    cta: 'Подати заявку на партнерство',
    download: 'Завантажити презентацію бренду'
  },
  en: {
    title: 'Become a CHAMALEON Partner',
    subtitle: 'Join the network of successful partners in 80 countries worldwide',
    cta: 'Apply for Partnership',
    download: 'Download Brand Presentation'
  }
};
export function PartnerCTA({
  language
}: PartnerCTAProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const t = sectionText[language];
  const partnerList = partners[language];
  const benefitList = benefits[language];
  return <section ref={ref} id="partner" className="relative py-16 sm:py-20 lg:py-32 bg-chamaleon-black overflow-hidden" data-id="element-214">
      {/* Background */}
      <div className="absolute inset-0" data-id="element-215">
        <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `repeating-linear-gradient(
            45deg,
            rgba(255,184,28,0.1),
            rgba(255,184,28,0.1) 2px,
            transparent 2px,
            transparent 20px
          )`
      }} data-id="element-216" />

        <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] rounded-full opacity-[0.08] pointer-events-none" style={{
        background: 'radial-gradient(circle, #FFB81C 0%, transparent 60%)'
      }} animate={{
        scale: [1, 1.1, 1],
        opacity: [0.08, 0.12, 0.08]
      }} transition={{
        duration: 8,
        repeat: Infinity
      }} data-id="element-217" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-218">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-8 sm:mb-12" data-id="element-219">
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-3 sm:mb-4 px-4" data-id="element-220">
            {t.title}
          </h2>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto px-4 mb-6" data-id="element-221">
            {t.subtitle}
          </p>
          <div className="w-20 h-0.5 bg-gradient-to-r from-transparent via-chamaleon-yellow to-transparent mx-auto" data-id="element-222" />
        </motion.div>

        {/* Benefits badges - Desktop only */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="hidden lg:flex flex-wrap justify-center gap-3 mb-12" data-id="element-223">
          {benefitList.map(benefit => <div key={benefit} className="flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full" data-id="element-224">
              <CheckCircleIcon className="w-4 h-4 text-chamaleon-yellow" data-id="element-225" />
              <span className="text-sm text-gray-300" data-id="element-226">{benefit}</span>
            </div>)}
        </motion.div>

        {/* Partner Cards - Unified style with Bestsellers */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12" data-id="element-227">
          {partnerList.map((partner, index) => {
          const Icon = partner.icon;
          return <motion.div key={partner.title} initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.3 + index * 0.1
          }} whileHover={{
            y: -8
          }} className="group" data-id="element-228">
                <div className="relative overflow-hidden rounded-3xl bg-chamaleon-yellow p-6 sm:p-8 h-full flex flex-col transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_15px_40px_rgba(255,184,28,0.3)]" data-id="element-229">
                  {/* Top Section: Icon */}
                  <div className="relative z-10 mb-6" data-id="element-230">
                    <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-chamaleon-black/5 border border-chamaleon-black/10 group-hover:bg-chamaleon-black/10 transition-all duration-500" data-id="element-231">
                      <Icon className="w-7 h-7 text-chamaleon-black" data-id="element-232" />
                    </div>
                  </div>

                  {/* Bottom Section: Content */}
                  <div className="relative z-10" data-id="element-233">
                    <h3 className="font-bold text-chamaleon-black mb-3 text-xl sm:text-2xl" data-id="element-234">
                      {partner.title}
                    </h3>
                    <p className="text-chamaleon-black/70 font-medium leading-relaxed text-sm sm:text-base" data-id="element-235">
                      {partner.description}
                    </p>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        {/* CTAs */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 px-4" data-id="element-236">
          <motion.a href="#" className="group relative inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-4 sm:py-5 bg-chamaleon-yellow text-chamaleon-black font-bold rounded-xl overflow-hidden text-base sm:text-lg" whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} data-id="element-237">
            <span className="relative z-10" data-id="element-238">{t.cta}</span>
            <ArrowRightIcon className="relative z-10 w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform" data-id="element-239" />
          </motion.a>

          <motion.a href="#" className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-4 sm:py-5 bg-transparent border-2 border-white/20 text-white font-medium rounded-xl hover:bg-white/5 hover:border-chamaleon-yellow transition-all text-sm sm:text-base" whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} data-id="element-240">
            <DownloadIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-241" />
            <span className="hidden sm:inline" data-id="element-242">{t.download}</span>
            <span className="sm:hidden" data-id="element-243">
              {language === 'ua' ? 'Презентація' : 'Presentation'}
            </span>
          </motion.a>
        </motion.div>
      </div>
    </section>;
}